import { autorun, observable, action, computed } from 'mobx'
import { create, persist } from 'mobx-persist'
import client from '../../../helpers/client'
import { asNormalizedMaps, asNormalizedArrays } from '../../../helpers/json-normalizer'
import { format, add, sub, startOfMonth, startOfWeek, startOfQuarter, startOfYear, endOfMonth, endOfYear, differenceInDays } from 'date-fns'
import flatMap from 'lodash/flatMap'

const DATE_PRESETS = [
  {value: "thismonth", label: "This month", validFor: ["scheduled_on", "created_on"]},
  {value: "last1month", label: "Last month", validFor: ["scheduled_on", "created_on"]},
  {value: "last3months", label: "Last 3 months", validFor: ["scheduled_on", "created_on"]},
  {value: "last12months", label: "Last 12 months", validFor: ["scheduled_on", "created_on"]},
  {value: "lastyear", label: "Last year", validFor: ["scheduled_on", "created_on"]},
  {value: "thisyear", label: "This year", validFor: ["scheduled_on", "created_on"]},
  {value: "nextyear", label: "Next year", validFor: ["scheduled_on"]},
  {value: "last3next3", label: "+/- 3 Months", validFor: ["scheduled_on"]},
  {value: "custom", label: "Custom", validFor: ["scheduled_on", "created_on"]},
]


class Store {
  @observable ready = false
  @observable loading = false
  @persist @observable chartType = "created_on"
  @persist @observable interval = "month"
  @persist @observable _datePreset = "last12months"
  @persist @observable brandId = ""
  @observable data
  @observable startDate
  @observable endDate

  constructor({ apiKey, dateFormat, showBrands, brands }) {
    this.apiKey = apiKey
    this.dateFormat = dateFormat
    this.showBrands = showBrands
    this.brands = brands
  }

  async setup() {
    if(this._datePreset == "custom") {
      this.datePreset = "last12months"
    }
    else {
      this.datePreset = this._datePreset // Set our default dates if they have it set to something like "last year"
    }
    await this.fetchReportData()
    this.ready = true

    autorun(() => {
      if(this.ready) {
        this.startDate, this.endDate, this.interval, this.chartType
        this.fetchReportData()
      }
    })
  }

  @computed get datePreset() {
    return this._datePreset
  }

  set datePreset(newDatePreset) {
    this._datePreset = newDatePreset
    switch(newDatePreset) {
      case "thismonth":
        this.startDate = startOfMonth(new Date())
        this.endDate = new Date()
        break;
      case "last1month":
        this.startDate = startOfMonth(sub(new Date(), {month: 1}))
        this.endDate = new Date()
        break;
      case "last3months":
        this.startDate = startOfMonth(sub(new Date(), {months: 2}))
        this.endDate = new Date()
        break;
      case "last12months":
        this.startDate = startOfMonth(sub(new Date(), {months: 11}))
        this.endDate = new Date()
        break;
      case "mtd":
        this.startDate = startOfMonth(new Date())
        this.endDate = new Date()
        break;
      case "qtd":
        this.startDate = startOfQuarter(new Date())
        this.endDate = new Date()
        break;
      case "ytd":
        this.startDate = startOfYear(new Date())
        this.endDate = new Date()
        break;
      case "last3next3":
        this.startDate = startOfMonth(sub(new Date(), {months: 3}))
        this.endDate = endOfMonth(add(new Date(), {months: 3}))
        break;
      case "lastyear":
        this.startDate = startOfYear(sub(new Date(), {years: 1}))
        this.endDate = endOfYear(this.startDate)
        break;
      case "thisyear":
        this.startDate = startOfYear(new Date())
        this.endDate = endOfYear(new Date())
        break;
      case "nextyear":
        this.startDate = startOfYear(add(new Date(), {years: 1}))
        this.endDate = endOfYear(this.startDate)
        break;
      default:
        console.log(newDatePreset)
    }
  }

  @computed get formattedStartDate() {
    if(!this.startDate) {
      return null
    }

    try {
      return format(this.startDate, this.dateFormat)
    }
    catch(e) {
      console.warn(e)
      return null
    }
  }

  @computed get formattedEndDate() {
    if(!this.endDate) {
      return null
    }

    try {
      return format(this.endDate, this.dateFormat)
    }
    catch(e) {
      console.warn(e)
      return null
    }
  }

  @computed get datePresets() {
    return DATE_PRESETS.filter(preset => preset.validFor.includes(this.chartType))
  }

  async fetchReportData() {
    const { interval, formattedStartDate, formattedEndDate } = this

    const currentPeriodParams = new URLSearchParams({
      api_key: this.apiKey,
      interval: interval,
      starts_at: formattedStartDate,
      ends_at: formattedEndDate,
      brand_id: this.brandId,
      chart_type: this.chartType
    })

    const currentPeriodLength = differenceInDays(this.endDate, this.startDate)

    const previousPeriodParams = new URLSearchParams({
      api_key: this.apiKey,
      interval: interval,
      starts_at: format(sub(this.startDate, {days: currentPeriodLength}), this.dateFormat),
      ends_at: format(sub(this.endDate, {days: currentPeriodLength}), this.dateFormat),
      brand_id: this.brandId,
      chart_type: this.chartType
    })

    this.loading = true

    const currentPeriodResponse = await client.get(`/api/v1/reporting/proposal_count_summary?${currentPeriodParams.toString()}`)
    const previousPeriodResponse = await client.get(`/api/v1/reporting/proposal_count_summary?${previousPeriodParams.toString()}`)

    console.log({ currentPeriodResponse: currentPeriodResponse.data.results })

    const mergedData = currentPeriodResponse.data.results.map((current, i) => {
      return {
        name: current.name,
        currentName: current.name,
        currentCount: current.proposalCount,
        previousName: previousPeriodResponse.data.results[i].name,
        previousCount: previousPeriodResponse.data.results[i].proposalCount
      }
    })

    console.log({ mergedData })

    this.data = mergedData
    this.loading = false
  }
}

export function createPersistedStore(persistenceKey, args) {
  if(persistenceKey) {
    const hydrate = create({})
    const store = new Store(args)
    hydrate(persistenceKey, store).then(() => store.setup())
    return store
  }
  else {
    const store = new Store(args)
    store.setup()
    return store
  }
}

export default Store
